import { reactive } from "@vue/composition-api";
import { AutocompleteResult } from "@/components/Autocomplete/autocomplete-id";
import { AcInventoryLotResult } from "@/components/Autocomplete/ac-inventory-lot";
import { IlsctlLineItem } from "@/models/IlsctlLineItem";
import { buildFormCommodityAutocomplete } from "@/models/Commodity";
import { buildFormClientAutocomplete } from "@/models/Client";
import { buildFormLotUnitAutocomplete } from "@/models/LotUnit";
import { WeightQuantityPriceStatus } from "@/models/WeightQuantityPriceStatus";

export interface hasInventoryLotId {
  inventoryLotId: string;
}

export const enum InventoryLotStatus {
  available = "available",
  completed = "completed",
  archived = "archived",
}

const prettifiedInventoryLotStatus = {
  available: "Available",
  completed: "Completed",
  archived: "Archived",
};

export const prettifyInventoryLotStatus = (s: InventoryLotStatus) => {
  return prettifiedInventoryLotStatus[s];
};

export const inventoryLotStatusReverseLookup = <
  { [key: string]: InventoryLotStatus }
>{
  available: InventoryLotStatus.available,
  completed: InventoryLotStatus.completed,
  archived: InventoryLotStatus.archived,
};

export interface InventoryLotApiParams {
  average_weight_lot_unit_id: string;
  client_id: string;
  commodity_id: string;
  default_sell_price: number;
  default_sell_price_status: WeightQuantityPriceStatus;
  description: string;
  end_date: string;
  id: string;
  lot_unit_id: string;
  purchase_price: number;
  purchase_price_status: WeightQuantityPriceStatus;
  quantity_status: WeightQuantityPriceStatus;
  start_date: string;
  start_quantity: number;
  start_weight: number;
  status: InventoryLotStatus;
  weight_default_sell_price: number;
  weight_default_sell_price_status: WeightQuantityPriceStatus;
  weight_lot_unit_id: string;
  weight_purchase_price: number;
  weight_purchase_price_status: WeightQuantityPriceStatus;
  weight_status: WeightQuantityPriceStatus;
}

export const InventoryLotNumberKeys: Array<keyof InventoryLot> = [
  "default_sell_price",
  "weight_default_sell_price",
  "purchase_price",
  "weight_purchase_price",
  "start_quantity",
  "start_weight",
  "income",
  "ratio_sold",
  "percent_sold",
  "calculated_end_weight",
  "average_weight",
  "remaining_quantity",
  "remaining_weight",
  "sold_weight",
  "sold_quantity",
];

export interface InventoryLot {
  average_weight_lot_unit_id: string;
  client_id: string;
  commodity_id: string;
  default_sell_price: number;
  default_sell_price_status: WeightQuantityPriceStatus;
  description: string;
  end_date: string;
  id: string;
  label: string;
  lot_unit_id: string;
  name: string;
  purchase_price: number;
  purchase_price_status: WeightQuantityPriceStatus;
  quantity_status: WeightQuantityPriceStatus;
  start_date: string;
  start_quantity: number;
  start_weight: number;
  status: InventoryLotStatus;
  weight_default_sell_price: number;
  weight_default_sell_price_status: WeightQuantityPriceStatus;
  weight_lot_unit_id: string;
  weight_purchase_price: number;
  weight_purchase_price_status: WeightQuantityPriceStatus;
  weight_status: WeightQuantityPriceStatus;

  income: number;
  ratio_sold: number;
  percent_sold: number;
  calculated_end_weight: number;
  average_weight: number;
  average_weight_lot_unit_label: string;
  client_label: string;
  commodity_color: string;
  commodity_label: string;
  lot_unit_label: string;
  remaining_quantity: number;
  remaining_weight: number;
  sold_quantity: number;
  sold_weight: number;
  weight_lot_unit_label: string;

  ilsctl_line_items: IlsctlLineItem[];
}

export interface FormInventoryLot extends InventoryLotApiParams {
  formCommodity: AutocompleteResult;
  formClient: AutocompleteResult;
  formLotUnit: AutocompleteResult;
  formWeightLotUnit: AutocompleteResult;
  formAverageWeightLotUnit: AutocompleteResult;
}

export function buildNewInventoryLot(): InventoryLot {
  return {
    average_weight_lot_unit_id: "",
    client_id: "",
    commodity_id: "",
    default_sell_price: 0,
    default_sell_price_status: WeightQuantityPriceStatus.estimated,
    description: "",
    end_date: "",
    id: "",
    label: "",
    lot_unit_id: "",
    name: "",
    purchase_price: 0,
    purchase_price_status: WeightQuantityPriceStatus.estimated,
    quantity_status: WeightQuantityPriceStatus.estimated,
    start_date: "",
    start_quantity: 0,
    start_weight: 0,
    status: InventoryLotStatus.available,
    weight_default_sell_price: 0,
    weight_default_sell_price_status: WeightQuantityPriceStatus.estimated,
    weight_lot_unit_id: "",
    weight_purchase_price: 0,
    weight_purchase_price_status: WeightQuantityPriceStatus.estimated,
    weight_status: WeightQuantityPriceStatus.estimated,

    income: 0,
    ratio_sold: 0,
    percent_sold: 0,
    calculated_end_weight: 0,
    average_weight: 0,
    average_weight_lot_unit_label: "",
    client_label: "",
    commodity_color: "",
    commodity_label: "",
    lot_unit_label: "",
    remaining_quantity: 0,
    remaining_weight: 0,
    sold_quantity: 0,
    sold_weight: 0,
    weight_lot_unit_label: "",

    ilsctl_line_items: [],
  };
}

export function buildFormInventoryLot() {
  let { formCommodity } = buildFormCommodityAutocomplete();
  let { formClient } = buildFormClientAutocomplete();
  let { formLotUnit } = buildFormLotUnitAutocomplete();

  let formInventoryLot = reactive<FormInventoryLot>({
    average_weight_lot_unit_id: "",
    client_id: "",
    commodity_id: "",
    default_sell_price: 0,
    default_sell_price_status: WeightQuantityPriceStatus.estimated,
    description: "",
    end_date: "",
    id: "",
    lot_unit_id: "",
    purchase_price: 0,
    purchase_price_status: WeightQuantityPriceStatus.estimated,
    quantity_status: WeightQuantityPriceStatus.estimated,
    start_date: "",
    start_quantity: 0,
    start_weight: 0,
    status: InventoryLotStatus.available,
    weight_default_sell_price: 0,
    weight_default_sell_price_status: WeightQuantityPriceStatus.estimated,
    weight_lot_unit_id: "",
    weight_purchase_price: 0,
    weight_purchase_price_status: WeightQuantityPriceStatus.estimated,
    weight_status: WeightQuantityPriceStatus.estimated,

    formCommodity: formCommodity,
    formClient: formClient,
    formLotUnit: formLotUnit(),
    formWeightLotUnit: formLotUnit(),
    formAverageWeightLotUnit: formLotUnit(),
  });

  let loadInventoryLotIntoForm = (il: InventoryLot, fil: FormInventoryLot) => {
    fil.average_weight_lot_unit_id = il.average_weight_lot_unit_id;
    fil.client_id = il.client_id;
    fil.commodity_id = il.commodity_id;
    fil.default_sell_price = il.default_sell_price;
    fil.default_sell_price_status = il.default_sell_price_status;
    fil.description = il.description;
    fil.end_date = il.end_date;
    fil.id = il.id;
    fil.lot_unit_id = il.lot_unit_id;
    fil.purchase_price = il.purchase_price;
    fil.purchase_price_status = il.purchase_price_status;
    fil.start_date = il.start_date;
    fil.start_quantity = il.start_quantity;
    fil.quantity_status = il.quantity_status;
    fil.start_weight = il.start_weight;
    fil.status = il.status;
    fil.weight_default_sell_price = il.weight_default_sell_price;
    fil.weight_default_sell_price_status = il.weight_default_sell_price_status;
    fil.weight_lot_unit_id = il.weight_lot_unit_id;
    fil.weight_purchase_price = il.weight_purchase_price;
    fil.weight_purchase_price_status = il.weight_purchase_price_status;
    fil.weight_status = il.weight_status;

    fil.formCommodity.label = il.commodity_label;
    fil.formCommodity.id = il.commodity_id;
    fil.formClient.label = il.client_label;
    fil.formClient.id = il.client_id;
    fil.formLotUnit.label = il.lot_unit_label;
    fil.formLotUnit.id = il.lot_unit_id;
    fil.formWeightLotUnit.label = il.weight_lot_unit_label;
    fil.formWeightLotUnit.id = il.weight_lot_unit_id;
    fil.formAverageWeightLotUnit.label = il.average_weight_lot_unit_label;
    fil.formAverageWeightLotUnit.id = il.average_weight_lot_unit_id;
  };

  let inventoryLotStatuses = reactive<InventoryLotStatus[]>([]);

  const loadInventoryLotStatusesIntoForm = (
    ilStatuses: InventoryLotStatus[],
  ) => {
    ilStatuses.push(
      InventoryLotStatus.available,
      InventoryLotStatus.completed,
      InventoryLotStatus.archived,
    );
  };

  return {
    formInventoryLot,
    loadInventoryLotIntoForm,
    inventoryLotStatuses,
    loadInventoryLotStatusesIntoForm,
  };
}

export function buildFormInventoryLotAutocomplete() {
  let formInventoryLot = () => {
    return reactive<AcInventoryLotResult>({
      id: "",
      label: "",
      clientLabel: "",
      commodityLabel: "",
      defaultSellPrice: 0,
      lotUnitLabel: "",
      lotUnitId: "",
    });
  };

  let updateInventoryLot = (
    evt: AcInventoryLotResult,
    formInventoryLot: AcInventoryLotResult,
    formData: hasInventoryLotId,
  ) => {
    formInventoryLot.id = evt.id;
    formInventoryLot.label = evt.label;
    formInventoryLot.clientLabel = evt.clientLabel;
    formInventoryLot.commodityLabel = evt.commodityLabel;
    formInventoryLot.defaultSellPrice = evt.defaultSellPrice;
    formInventoryLot.lotUnitLabel = evt.lotUnitLabel;
    formInventoryLot.lotUnitId = evt.lotUnitId;
    formData.inventoryLotId = evt.id;
  };

  return {
    formInventoryLot,
    updateInventoryLot,
  };
}
